import { Box, CardInfo, NoticeBox, Text, useToggle } from '@factorialco/deprecated-design-system'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'
import MoneyWithCurrency from 'modules/core/lib/money'

import { CardSidepanel } from 'modules/banking/components/CardSidepanel'
import { isActive } from 'modules/banking/lib/cards/isActive'
import { isCanceled } from 'modules/banking/lib/cards/isCanceled'
import DataUnavailable from 'modules/software_management/components/ApplicationDetailsContent/DataUnavailable'
import DetailsWidget from 'modules/software_management/components/ApplicationDetailsContent/DetailsWidget'
import InvoiceBanner from 'modules/software_management/components/ApplicationDetailsContent/InvoiceBanner'
import CreateCardModal from 'modules/software_management/components/Cards/CreateModal'
import NewCard from 'modules/software_management/components/Cards/New'
import CardWidget from 'modules/software_management/components/Cards/Widget'
import CardsBanner from 'modules/software_management/components/CardsBanner'
import LineChart from 'modules/software_management/components/SoftwareDashboard/_LineChart'
import { useApplicationDetailsRouterParams } from 'modules/software_management/hooks/useApplicationDetailsRouterParams'
import { monthlySoftwareCosts, softwareCostForMonth } from 'modules/software_management/lib/costs'
import { currentMonth, previousMonth } from 'modules/software_management/lib/dates'
import { useApplicationsContext } from 'modules/software_management/pages/ApplicationsPage'
import { EditApplicationModal } from 'modules/software_management/pages/EditApplicationModal'

export const Overview = () => {
  const { navigation } = useNavigation()
  const {
    access: { locale },
  } = useCurrentSession()
  const { accountProvidersAvailable, bankAccount, onboardingInProgress } = useApplicationsContext()

  const { softwareSetup, card, canManagePurchaseFinancialDocuments, currency, softwareCosts } =
    useApplicationDetailsRouterParams()

  const [showCreateCardModal, toggleCreateCardModal] = useToggle(false)
  const [editModalOpen, toggleEditModal] = useToggle(false)
  const [showCardDetail, toggleCardDetail] = useToggle(false)

  const companyId = useCurrentSession().company.id

  const showCardReminder = !softwareSetup.hasPayments && !!card && isActive(card)
  const showCardsBanner = !bankAccount && accountProvidersAvailable

  const costGraphData = monthlySoftwareCosts(softwareCosts)

  const currentSpend = softwareCostForMonth(softwareCosts, currentMonth())
  const previousSpend = softwareCostForMonth(softwareCosts, previousMonth())

  const spendChange = previousSpend > 0 ? currentSpend - previousSpend : 0
  const currentValue = MoneyWithCurrency({
    amount: currentSpend,
    currency,
    locale,
  })

  return (
    <Box paddingTop="s32" gap="s32">
      {canManagePurchaseFinancialDocuments && (
        <InvoiceBanner
          buttonUrl={navigation.finance.spending.invoices.inbox.fullPath()}
          urlLink="https://help.factorialhr.com/finance/tracking-software-costs-with-the-invoice-tracking-system"
        />
      )}
      {showCardReminder && (
        <NoticeBox
          color="viridian200"
          message={
            <Text.Normal>
              {i18n.t('software_management.software_setups.cards.new_card_reminder')}
            </Text.Normal>
          }
        />
      )}
      <Box gap="s32" flexDirection="row">
        {costGraphData.length === 0 ? (
          <CardInfo>
            <DataUnavailable />
          </CardInfo>
        ) : (
          <Box width="full">
            <LineChart
              data={costGraphData}
              title={i18n.t('software_management.software_setups.costs.graphs.cost_graph.title')}
              subtitle={i18n.t(
                'software_management.software_setups.costs.graphs.cost_graph.subtitle'
              )}
              serieId={i18n.t(
                'software_management.software_setups.dashboard.charts.cost_per_month.tooltip'
              )}
              currentValue={currentValue}
              valueChange={spendChange}
              currency={currency}
            />
          </Box>
        )}

        <Box width="full" gap="s24">
          <DetailsWidget onEdit={toggleEditModal(true)} softwareSetup={softwareSetup} />

          {!!card && !isCanceled(card) ? (
            <CardWidget
              card={card}
              currency={card.bankAccount.currency}
              onClick={toggleCardDetail(true)}
            />
          ) : (
            <NewCard openNewCardModal={toggleCreateCardModal(true)} />
          )}
          {showCardsBanner && <CardsBanner fullSize showCompleteAccount={onboardingInProgress} />}
        </Box>

        {editModalOpen && (
          <EditApplicationModal
            softwareSetupId={softwareSetup.id}
            companyId={companyId}
            onClose={toggleEditModal(false)}
          />
        )}

        {showCreateCardModal && (
          <CreateCardModal
            onClose={toggleCreateCardModal(false)}
            softwareSetupId={softwareSetup.id}
          />
        )}

        {showCardDetail && card && (
          <CardSidepanel
            cardId={card.id}
            onClose={toggleCardDetail(false)}
            skipCardControlsCategories
          />
        )}
      </Box>
    </Box>
  )
}
